import axios from 'axios';
import {
  adaptHockeyMutationResult,
  adaptRemindersRead,
  adaptSkillTestingQuestion,
  adaptStqStatus,
  createServerError,
  createUnauthorizedError,
  isHockeyThrownResultCode,
  isUnauthenticatedGraphqlError,
  unwrapHockeyResult,
} from './adapters';
import { getMockResponse } from '../boomMocks';
import { ME_BOOM_FIELD, MUTATIONS, QUERIES } from './operations';
import {
  HOCKEY_CHALLENGE_USE_CASE,
  isPlatformTransportFailure,
  isValidPlatformQuestion,
} from '../../stq/platformQuestion';

const SHARED_STQ_ERROR_CODES = new Set(['FEATURE_DISABLED', 'STQ_UNAVAILABLE', 'BAD_USER_INPUT']);

const LEADERBOARD_ROUTES = new Set([
  'leaderboard',
  'leaderboardLocal',
  'seasonLeaderboard',
  'seasonLeaderboardLocal',
  'postSeasonLeaderboard',
  'postSeasonLeaderboardLocal',
]);

/** Reads that accept `HockeyMockInput` (mutations and `user` do not). */
const MOCKABLE_ROUTES = new Set(['players', 'pickHistory', 'userStats']);

function buildLeaderboardInput(params = {}) {
  const input = {};
  if (params.challengeNumber != null) {
    input.challengeNumber = Number(params.challengeNumber);
  }
  if (params.season != null) {
    input.season = String(params.season);
  }
  if (params.mockResponse != null) {
    input.mockResponse = String(params.mockResponse);
  }
  return Object.keys(input).length ? input : undefined;
}

function remindersOptInFromData(data = {}) {
  const raw =
    data.attributes?.tnhc_user_reminders ??
    data.tnhc_user_reminders ??
    data.tnhcUserReminders ??
    data.hockeyRemindersOptIn;
  return raw === true || raw === 'true';
}

function buildGraphqlPayload({ method, route, data, params }) {
  const isWrite = method === 'POST' || method === 'PATCH';

  if (route === 'skillTestingQuestion') {
    return {
      query: QUERIES.skillTestingQuestion,
      operationName: 'SkillTestingQuestion',
      variables: { useCase: HOCKEY_CHALLENGE_USE_CASE },
    };
  }

  if (route === 'skillTestingAnswer') {
    return {
      query: MUTATIONS.skillTestingAnswer,
      operationName: 'SubmitSkillTestingAnswer',
      variables: {
        input: {
          useCase: data?.useCase || HOCKEY_CHALLENGE_USE_CASE,
          choiceId: String(data.choiceId),
        },
      },
    };
  }

  if (route === 'quiz') {
    if (isWrite) {
      return {
        query: MUTATIONS.quiz,
        operationName: 'RecordSkillTestingQuestion',
        variables: {
          input: {
            eventId: data.eventId,
            failed: Boolean(data.failed),
          },
        },
      };
    }
    return {
      query: QUERIES.quiz,
      operationName: 'SkillTestingQuestionStatus',
      variables: { eventId: params.eventId },
    };
  }

  if (route === 'notification') {
    // REST used POST for both read and write; treat presence of preference payload as write.
    const hasWritePayload =
      data &&
      (data.attributes?.tnhc_user_reminders != null ||
        data.tnhc_user_reminders != null ||
        data.tnhcUserReminders != null ||
        data.hockeyRemindersOptIn != null);

    if (hasWritePayload) {
      return {
        query: MUTATIONS.notification,
        operationName: 'UpdateHockeyReminders',
        variables: {
          input: {
            hockeyRemindersOptIn: remindersOptInFromData(data),
          },
        },
      };
    }
    return {
      query: QUERIES.notification,
      operationName: 'HockeyRemindersOptIn',
      variables: {},
    };
  }

  if (route === 'playerPicks' && isWrite) {
    return {
      query: MUTATIONS.playerPicks,
      operationName: 'SubmitHockeyPicks',
      variables: {
        input: {
          picks: (data.picks || []).map((pick) => ({
            setId: String(pick.setId),
            playerId: String(pick.playerId),
          })),
        },
      },
    };
  }

  if (route === 'user' && isWrite) {
    return {
      query: MUTATIONS.user,
      operationName: 'UpdateHockeyUser',
      variables: {
        input: {
          username: data.username,
        },
      },
    };
  }

  if (QUERIES[route]) {
    const variables = {};
    // Explicit params win so callers can still drive a scenario directly.
    const mockResponse = params.mockResponse ?? getMockResponse(route);

    if (LEADERBOARD_ROUTES.has(route)) {
      const input = buildLeaderboardInput({ ...params, mockResponse });
      if (input) variables.input = input;
    } else if (MOCKABLE_ROUTES.has(route) && mockResponse != null) {
      variables.input = { mockResponse: String(mockResponse) };
    }

    return {
      query: QUERIES[route],
      variables,
    };
  }

  throw new Error(`No GraphQL mapping for route: ${route}`);
}

function isSharedStqRoute(route) {
  return route === 'skillTestingQuestion' || route === 'skillTestingAnswer';
}

function throwGraphqlErrors(errors) {
  if (isUnauthenticatedGraphqlError(errors)) {
    throw createUnauthorizedError();
  }
  const code = errors[0]?.extensions?.code;
  if (SHARED_STQ_ERROR_CODES.has(code)) {
    const err = new Error(errors[0].message || code);
    err.code = code;
    throw err;
  }
  throw createServerError();
}

function resolveGraphqlResult({ route, method, data, body }) {
  const adapted = adaptGraphqlData({
    route,
    method,
    data: body?.data,
    rawRequestData: data,
  });

  if (isSharedStqRoute(route) && isValidPlatformQuestion(adapted)) {
    return adapted;
  }

  if (body?.errors?.length) {
    throwGraphqlErrors(body.errors);
  }

  return adapted;
}

function adaptGraphqlData({ route, method, data, rawRequestData }) {
  const isWrite = method === 'POST' || method === 'PATCH';

  if (route === 'skillTestingQuestion') {
    return adaptSkillTestingQuestion(data?.me?.skillTestingQuestion);
  }

  if (route === 'skillTestingAnswer') {
    return adaptSkillTestingQuestion(data?.submitSkillTestingAnswer);
  }

  if (route === 'quiz') {
    const status = isWrite ? data?.recordSkillTestingQuestion : data?.me?.skillTestingQuestionStatus;
    return adaptStqStatus(status);
  }

  if (route === 'notification') {
    const hasWritePayload =
      rawRequestData &&
      (rawRequestData.attributes?.tnhc_user_reminders != null ||
        rawRequestData.tnhc_user_reminders != null ||
        rawRequestData.tnhcUserReminders != null ||
        rawRequestData.hockeyRemindersOptIn != null);

    if (hasWritePayload) {
      return Boolean(data?.updateHockeyReminders);
    }
    return adaptRemindersRead(data?.me?.hockeyRemindersOptIn);
  }

  if (route === 'playerPicks' && isWrite) {
    return unwrapHockeyResult(data?.submitHockeyPicks);
  }

  if (route === 'user' && isWrite) {
    return adaptHockeyMutationResult(data?.updateHockeyUser);
  }

  const field = ME_BOOM_FIELD[route];
  if (field) {
    return unwrapHockeyResult(data?.me?.[field]);
  }

  return data;
}

/**
 * POST to the federated gateway. Identity comes from the JWT only —
 * do not send x-loyalty-id / x-user-id / x-cognito-id.
 */
export async function makeGraphqlRequest({
  graphqlUrl,
  method = 'GET',
  language = 'en',
  platform = 'web',
  currentSession,
  route,
  data,
  params = {},
}) {
  const payload = buildGraphqlPayload({ method, route, data, params });

  try {
    const response = await axios({
      method: 'POST',
      url: graphqlUrl,
      data: {
        query: payload.query,
        variables: payload.variables || {},
        ...(payload.operationName ? { operationName: payload.operationName } : {}),
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentSession}`,
        'x-ui-region': 'CA',
        'x-ui-language': language,
        'x-ui-platform': platform,
      },
    });

    return resolveGraphqlResult({
      route,
      method,
      data,
      body: response.data,
    });
  } catch (err) {
    let nextError = err;
    if (isSharedStqRoute(route) && err?.response?.data) {
      try {
        return resolveGraphqlResult({
          route,
          method,
          data,
          body: err.response.data,
        });
      } catch (resolved) {
        nextError = resolved;
      }
    }
    if (nextError?.status === 401 || nextError?.code === 500) {
      throw nextError;
    }
    if (
      nextError?.code === 'NotAllowed' ||
      SHARED_STQ_ERROR_CODES.has(nextError?.code) ||
      isHockeyThrownResultCode(nextError?.code)
    ) {
      throw nextError;
    }
    if (isPlatformTransportFailure(nextError) || nextError?.isNetworkError || nextError?.message === 'Network Error') {
      const networkErr = new Error(nextError.message || 'Network Error');
      networkErr.isNetworkError = true;
      if (nextError.code != null) {
        networkErr.code = nextError.code;
      }
      if (nextError.status != null) {
        networkErr.status = nextError.status;
      }
      throw networkErr;
    }
    if (nextError?.response?.status === 401) {
      throw createUnauthorizedError();
    }
    throw createServerError();
  }
}
